import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Clock, Shield, Star } from 'lucide-react';
import ContactButton from '../components/ContactButton';

const PricingPolicy = () => {
  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
                alt="PRSVR Logo" 
                className="h-10"
                style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
              />
            </a>
            <Button onClick={() => window.location.href = '/'} variant="ghost" className="text-cream">
              Back to Home
            </Button>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-6 pt-32 pb-16 max-w-4xl">
        <h1 className="text-5xl font-black text-charcoal uppercase mb-8">Pricing & Inventory Policy</h1>
        <p className="text-warmGray mb-8">Effective Date: {new Date().toLocaleDateString()}</p>

        <div className="space-y-8 text-charcoal">
          <Card className="border-4 border-charcoal">
            <CardContent className="p-8">
              <h2 className="text-3xl font-bold mb-6 text-charcoal">Our Pricing and Inventory Policy</h2>
              <p className="text-warmGray leading-relaxed mb-6">
                Most of our items are kept in stock for quick delivery. However, for certain newer models or items that require specialized production, we may operate on a made-to-order basis.
              </p>
              <p className="text-warmGray leading-relaxed">
                When an item is made-to-order, it is created specifically after you place your order. This process may require a lead time of up to 3 weeks for delivery.
              </p>
            </CardContent>
          </Card>

          <section>
            <h2 className="text-3xl font-bold mb-6">Why This Method is Used (The Benefits to You)</h2>
            <p className="text-warmGray leading-relaxed mb-6">
              When we utilize the made-to-order approach for these select items, it provides two main advantages for you:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <Card className="border-4 border-charcoal bg-cream">
                <CardContent className="p-6">
                  <Shield className="h-10 w-10 text-warmSage mb-4" />
                  <h3 className="text-xl font-bold mb-3 text-charcoal">Stable Pricing and Value</h3>
                  <p className="text-warmGray leading-relaxed">
                    By eliminating the risk of unclaimed inventory on these specialized items, we avoid the need for frequent clearance sales. This means when you purchase one of these pieces, you can trust that you are getting it at a consistent, fair price, and its original value is protected—you won't see it marked down significantly just a week later.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-4 border-charcoal bg-cream">
                <CardContent className="p-6">
                  <Star className="h-10 w-10 text-warmSage mb-4" />
                  <h3 className="text-xl font-bold mb-3 text-charcoal">Exclusivity and Uniqueness</h3>
                  <p className="text-warmGray leading-relaxed">
                    The made-to-order process significantly reduces the chance that you will encounter others with the exact same newer or specialized item, especially at a special occasion. This helps ensure your purchase remains unique.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          <Card className="border-4 border-charcoal bg-charcoal text-cream">
            <CardContent className="p-8">
              <Clock className="h-12 w-12 text-warmSage mb-4 mx-auto" />
              <h3 className="text-2xl font-bold mb-4 text-center">Delivery Timeline</h3>
              <ul className="space-y-3 text-cream/90">
                <li className="flex items-start">
                  <span className="text-warmSage mr-2">•</span>
                  <span><strong>In-Stock Items:</strong> Ship within 2-3 business days</span>
                </li>
                <li className="flex items-start">
                  <span className="text-warmSage mr-2">•</span>
                  <span><strong>Made-to-Order Items:</strong> Up to 3 weeks for production and delivery</span>
                </li>
                <li className="flex items-start">
                  <span className="text-warmSage mr-2">•</span>
                  <span><strong>Custom Uniforms:</strong> 3-5 weeks (10-12 days with rush order)</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <section>
            <h2 className="text-3xl font-bold mb-6">How You'll Know</h2>
            <p className="text-warmGray leading-relaxed mb-4">
              Product pages will clearly indicate whether an item is in stock or made-to-order. If an item requires production time, the estimated delivery window will be displayed before you complete your purchase.
            </p>
            <p className="text-warmGray leading-relaxed">
              You will receive order confirmation and production updates via email throughout the process.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Questions?</h2>
            <div className="bg-charcoal text-cream p-8 rounded-lg">
              <p className="mb-4">If you have questions about our pricing and inventory policy or need to check if a specific item is in stock:</p>
              <div className="space-y-2">
                <p className="font-bold">Contact PRSVR</p>
                <p>Email: info@prsvr.com</p>
                <p>Phone: (661) 553-2186</p>
              </div>
              <Button 
                onClick={() => window.location.href = 'mailto:info@prsvr.com'}
                className="mt-6 bg-warmSage text-charcoal hover:bg-warmSage/90 font-bold"
              >
                Email Us
              </Button>
            </div>
          </section>
        </div>
      </div>

      <ContactButton />
    </div>
  );
};

export default PricingPolicy;
